import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  previewPaymentImport, commitPaymentImport,
  type PreviewResult, type PaymentCategory,
} from '../api';
import { Button } from '../components/ui';

const CATEGORY_LABEL: Record<PaymentCategory, string> = {
  rental: 'Udlejning', extra: 'Ekstra betaling', credit: 'Modtagne penge',
  cancellation: 'Aflysningsgebyr', refusion: 'Refusion', subscription: 'Abonnement',
  boost: 'Boost', bank: 'Bank-udbetaling', owed: 'Skyldigt beløb', other: 'Andet',
};

const fmtAmount = (n: number) => n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTHS_DK = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];
const fmtDate = (iso: string) => {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${parseInt(m[3])}. ${MONTHS_DK[parseInt(m[2])-1]} ${m[1]}`;
};

export default function GoMoreImportPage() {
  const nav = useNavigate();
  const [files, setFiles]     = useState<File[]>([]);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFiles(Array.from(e.target.files || []));
    setPreview(null);
    setError(null);
  };

  const runPreview = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const result = await previewPaymentImport(files);
      setPreview(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload-fejl');
    } finally {
      setUploading(false);
    }
  };

  const runCommit = async () => {
    if (!preview) return;
    setImporting(true);
    setError(null);
    try {
      // Backend sender posting_index direkte i allocations, så vi kan bygge commit-payload trivielt.
      // Umatchede sendes IKKE med: backend afgør selv hvad der må ignoreres (udbetalinger, abonnement)
      // og lægger resten i match-køen. Før blev alt umatchet sendt som 'ignored' og forsvandt.
      const allocations = preview.allocations.map(a => ({
        posting_index: a.posting_index, booking_id: a.booking_id, match_type: 'auto' as const,
      }));
      const result = await commitPaymentImport({ postings: preview.postings_payload, allocations });
      alert(`Importerede ${result.inserted} posteringer (${result.skipped} sprunget over som dubletter).`);
      nav('/gomore');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import-fejl');
    } finally {
      setImporting(false);
    }
  };

  return (
    <main className="px-4 md:px-8 py-6 max-w-[1200px] mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/gomore" className="text-ink-500 hover:text-ink-900"><ArrowLeft size={20} /></Link>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Importér kontoudtog</h1>
          <p className="text-sm text-ink-500 mt-0.5">Upload GoMore JSON-fil(er) — match køres automatisk, du bekræfter inden gem.</p>
        </div>
      </div>

      {/* Upload-zone */}
      <div className="list-card p-6 mb-4">
        <div className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-2">
              Vælg JSON-fil(er) fra GoMore-konto
            </label>
            <input ref={inputRef} type="file" accept=".json,application/json" multiple onChange={handleFileSelect}
              className="block w-full text-sm text-ink-700 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0
                         file:text-sm file:font-medium file:bg-sage-100 file:text-sage-700 hover:file:bg-sage-200" />
            <p className="text-xs text-ink-500 mt-1">
              Du kan vælge flere måneder på én gang. Find dem i <code className="text-[11px]">L:\579 ApS\Regnskab\GoMore Konto\json\</code>.
            </p>
          </div>
          {files.length > 0 && !preview && (
            <div className="text-sm bg-cream-100 border border-ink-100 rounded p-3">
              <strong>{files.length} fil(er) valgt:</strong>
              <ul className="mt-1 text-xs text-ink-700 list-disc pl-5">
                {files.slice(0, 8).map((f, i) => <li key={i}>{f.name} ({(f.size / 1024).toFixed(0)} KB)</li>)}
                {files.length > 8 && <li>+ {files.length - 8} flere</li>}
              </ul>
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="sage" iconLeft={<Upload size={15} />} onClick={runPreview} disabled={files.length === 0 || uploading}>
              {uploading ? 'Analyserer…' : `Analysér ${files.length > 0 ? `(${files.length} fil)` : ''}`}
            </Button>
            {files.length > 0 && (
              <Button variant="ghost" onClick={() => { setFiles([]); if (inputRef.current) inputRef.current.value = ''; setPreview(null); setError(null); }}>
                Ryd
              </Button>
            )}
          </div>
        </div>
        {error && (
          <div className="mt-3 text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded p-3">
            <AlertCircle size={14} className="inline mr-1" />{error}
          </div>
        )}
      </div>

      {/* Preview */}
      {preview && (
        <>
          <div className="list-card p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-ink-900">Resultat af analyse</div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setPreview(null); setFiles([]); if (inputRef.current) inputRef.current.value = ''; }}>
                  Annullér
                </Button>
                <Button variant="sage" size="sm" iconLeft={<CheckCircle2 size={14} />} onClick={runCommit} disabled={importing}>
                  {importing ? 'Gemmer…' : `Gem ${preview.new_count} posteringer`}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Stat label="Nye posteringer" value={preview.new_count} />
              <Stat label="Allerede importeret (sprunget over)" value={preview.duplicate_count} />
              <Stat label="Kunne ikke importeres" value={preview.skipped_count ?? 0}
                color={preview.skipped?.some(s => s.reason !== 'pending') ? 'missing' : undefined} />
              <Stat label="Auto-matched til booking" value={preview.allocations.filter(a => a.booking_id !== null).length} />
              <Stat label="Ikke matched / ignoreres" value={preview.unmatched.length} />
              <Stat label="Bookings i balance" value={preview.summary.bookings_balanced} color="ok" />
              <Stat label="Bookings mangler betaling" value={preview.summary.bookings_missing} color="missing" />
              <Stat label="Bookings overskud" value={preview.summary.bookings_overflow} color="overflow" />
              <Stat label="Netto-balance"
                value={`${fmtAmount(preview.summary.net_balance)} DKK`}
                color={preview.summary.net_balance < 0 ? 'missing' : preview.summary.net_balance > 0 ? 'overflow' : 'ok'} />
            </div>
          </div>

          {/* Ikke importeret — vises altid når noget blev frasorteret, så rækker
              ikke kan forsvinde ubemærket (fx udbetalinger under overførsel). */}
          {preview.skipped_count > 0 && (
            <div className="list-card overflow-hidden mb-4">
              <div className="px-4 py-3 border-b border-ink-100 text-sm font-semibold">
                Kunne ikke importeres ({preview.skipped_count})
              </div>
              <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                <table className="data-table w-full">
                  <thead>
                    <tr>
                      <th className="text-left px-3 py-2">Dato</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-left px-3 py-2">Bil / konto</th>
                      <th className="text-right px-3 py-2">Beløb</th>
                      <th className="text-left px-3 py-2">Årsag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.skipped.map((s, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2">{s.dato ?? '—'}</td>
                        <td className="px-3 py-2">{s.beskrivelse ?? '—'}</td>
                        <td className="px-3 py-2">{s.nummerplade ?? '—'}</td>
                        <td className="px-3 py-2 text-right">{s.beloeb !== null ? fmtAmount(s.beloeb) : '—'}</td>
                        <td className="px-3 py-2">
                          {s.reason === 'pending'
                            ? <span className="text-ink-500">Afventer udbetaling</span>
                            : <span className="text-rose-700">{s.reason === 'no_date' ? 'Ingen læsbar dato' : 'Intet beløb'}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-2 text-xs text-ink-500 border-t border-ink-100">
                Afventende posteringer kommer med automatisk når GoMore har udbetalt dem.
                Andre årsager bør undersøges — de forsvinder ellers uden spor.
              </div>
            </div>
          )}

          {/* Allokeringer */}
          <div className="list-card overflow-hidden mb-4">
            <div className="px-4 py-3 border-b border-ink-100 text-sm font-semibold">
              Posteringer der matches mod en booking ({preview.allocations.filter(a => a.booking_id !== null).length})
            </div>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th className="text-left px-3 py-2">Dato</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Lejer</th>
                    <th className="text-left px-3 py-2">Bil</th>
                    <th className="text-right px-3 py-2">Beløb</th>
                    <th className="text-left px-3 py-2">Match</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.allocations.filter(a => a.booking_id !== null).map((a, i) => (
                    <tr key={i} className="row-hover">
                      <td className="px-3 py-1.5 whitespace-nowrap text-sm">{fmtDate(a.posting.posting_date)}</td>
                      <td className="px-3 py-1.5 text-sm">{CATEGORY_LABEL[a.posting.category]}</td>
                      <td className="px-3 py-1.5 text-sm">{a.posting.renter_name || <span className="text-ink-400">—</span>}</td>
                      <td className="px-3 py-1.5 text-sm whitespace-nowrap">{a.posting.registration_number || <span className="text-ink-400">—</span>}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums text-sm whitespace-nowrap ${a.posting.amount < 0 ? 'text-rose-700' : ''}`}>
                        {fmtAmount(a.posting.amount)}
                      </td>
                      <td className="px-3 py-1.5 text-sm">
                        <span className="text-xs px-1.5 py-0.5 rounded bg-sage-100 text-sage-700">Booking #{a.booking_id}</span>
                        <span className="text-[11px] text-ink-400 ml-1.5">{a.match_confidence}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Ikke-matched / ignoreret */}
          {preview.unmatched.length > 0 && (
            <div className="list-card overflow-hidden">
              <div className="px-4 py-3 border-b border-ink-100 text-sm font-semibold">
                Ikke matched eller markeret som ignoreret ({preview.unmatched.length})
              </div>
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="data-table w-full">
                  <thead>
                    <tr>
                      <th className="text-left px-3 py-2">Dato</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-left px-3 py-2">Lejer / Detalje</th>
                      <th className="text-left px-3 py-2">Bil</th>
                      <th className="text-right px-3 py-2">Beløb</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.unmatched.map((p, i) => (
                      <tr key={i} className="row-hover">
                        <td className="px-3 py-1.5 whitespace-nowrap text-sm">{fmtDate(p.posting_date)}</td>
                        <td className="px-3 py-1.5 text-sm">
                          {CATEGORY_LABEL[p.category]}
                          <div className="text-[11px] text-ink-400">{p.description}</div>
                        </td>
                        <td className="px-3 py-1.5 text-sm">{p.renter_name || <span className="text-ink-400">—</span>}</td>
                        <td className="px-3 py-1.5 text-sm whitespace-nowrap">{p.registration_number || <span className="text-ink-400">—</span>}</td>
                        <td className={`px-3 py-1.5 text-right tabular-nums text-sm whitespace-nowrap ${p.amount < 0 ? 'text-rose-700' : ''}`}>
                          {fmtAmount(p.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </main>
  );
}

function Stat({ label, value, color }: { label: string; value: number | string; color?: 'ok' | 'missing' | 'overflow' }) {
  const colorClass = color === 'missing' ? 'text-rose-700' : color === 'overflow' ? 'text-amber-700' : color === 'ok' ? 'text-sage-700' : 'text-ink-900';
  return (
    <div>
      <div className="text-xs text-ink-500">{label}</div>
      <div className={`text-lg font-semibold ${colorClass}`}>{value}</div>
    </div>
  );
}
